import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterBar, type FilterOption } from '@/components/shared/FilterBar';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import {
  DataValidationKpiCards,
  type DataValidationKpiFilter,
} from '@/modules/forecast-review/components/DataValidationKpiCards';
import {
  DataValidationProductsTable,
  type DataValidationSelectedProduct,
} from '@/modules/forecast-review/components/DataValidationProductsTable';
import { DataValidationProductPanel } from '@/modules/forecast-review/components/DataValidationProductPanel';
import {
  forecastReviewService,
  type ForecastReviewDataValidationProductSeriesResponse,
  type ForecastReviewDataValidationProductsResponse,
  type ForecastReviewDataValidationQueryParams,
  type ForecastReviewDataValidationSummary,
  type ForecastReviewFilterOptions,
} from '@/services/forecastReviewService';

type AnomalyTypeFilter = 'stockout' | 'demand_outlier';

function kpiFilterToHasParams(filter: DataValidationKpiFilter): Pick<
  ForecastReviewDataValidationQueryParams,
  'has_gaps' | 'has_stockout' | 'has_outlier'
> {
  if (filter === 'gaps') return { has_gaps: true };
  if (filter === 'stockout') return { has_stockout: true };
  if (filter === 'outlier') return { has_outlier: true };
  return {};
}

export function ForecastDataValidationPage() {
  const { t } = useTranslation();
  const warehouseId = useWarehouseId();
  const [filterOptions, setFilterOptions] = useState<ForecastReviewFilterOptions | null>(null);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [activeFilters, setActiveFilters] = useState<Array<{ id: string; label: string; value: string }>>([]);
  const [kpiFilter, setKpiFilter] = useState<DataValidationKpiFilter>('all');
  const [summary, setSummary] = useState<ForecastReviewDataValidationSummary | null>(null);
  const [products, setProducts] = useState<ForecastReviewDataValidationProductsResponse | null>(null);
  const [productsPage, setProductsPage] = useState(1);
  const [productsPageSize, setProductsPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [selected, setSelected] = useState<DataValidationSelectedProduct | null>(null);
  const [series, setSeries] = useState<ForecastReviewDataValidationProductSeriesResponse | null>(null);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [seriesError, setSeriesError] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const anomalyTypeFilter = (filters.anomaly_type?.[0] as AnomalyTypeFilter | undefined) ?? undefined;

  const queryParams = useMemo((): ForecastReviewDataValidationQueryParams => {
    const familyId = filters.family?.[0];
    const subfamilyId = filters.subfamily?.[0];
    const brandId = filters.brand?.[0];
    const supplierId = filters.supplier?.[0];
    const productId = filters.product?.[0];
    const brandCode = filters.brand_code?.[0];

    return {
      warehouse_id: typeof warehouseId === 'number' ? warehouseId : undefined,
      family_id: familyId ? Number(familyId) : undefined,
      subfamily_id: subfamilyId ? Number(subfamilyId) : undefined,
      brand_id: brandId ? Number(brandId) : undefined,
      brand_code: brandCode || undefined,
      supplier_id: supplierId ? Number(supplierId) : undefined,
      product_id: productId ? Number(productId) : undefined,
      anomaly_type: anomalyTypeFilter,
      ...kpiFilterToHasParams(kpiFilter),
    };
  }, [filters, warehouseId, anomalyTypeFilter, kpiFilter]);

  const periodUnit = summary?.period_unit ?? 'week';

  const warehouseLabel = useMemo(() => {
    if (typeof warehouseId !== 'number' || !filterOptions) return null;
    return filterOptions.warehouses.find((w) => w.id === warehouseId)?.name ?? null;
  }, [filterOptions, warehouseId]);

  const seriesQueryParams = useMemo(
    (): ForecastReviewDataValidationQueryParams => ({
      warehouse_id: typeof warehouseId === 'number' ? warehouseId : undefined,
    }),
    [warehouseId]
  );

  const loadFilters = useCallback(async () => {
    if (typeof warehouseId !== 'number') return;
    try {
      const data = await forecastReviewService.getFilters(queryParams);
      setFilterOptions(data);
    } catch {
      setFilterOptions(null);
    }
  }, [queryParams, warehouseId]);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const data = await forecastReviewService.getDataValidationSummary(queryParams);
      setSummary(data);
    } catch {
      setSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  }, [queryParams]);

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const data = await forecastReviewService.getDataValidationProducts({
        ...queryParams,
        page: productsPage,
        page_size: productsPageSize,
      });
      setProducts(data);
    } catch {
      setProducts(null);
    } finally {
      setLoadingProducts(false);
    }
  }, [queryParams, productsPage, productsPageSize]);

  useEffect(() => {
    void loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    if (typeof warehouseId !== 'number') return;
    setProductsPage(1);
    setSelected(null);
    void loadSummary();
  }, [warehouseId, loadSummary]);

  useEffect(() => {
    if (typeof warehouseId !== 'number') return;
    setProductsPage(1);
    setSelected(null);
  }, [warehouseId, queryParams]);

  useEffect(() => {
    if (typeof warehouseId !== 'number') return;
    void loadProducts();
  }, [warehouseId, loadProducts]);

  useEffect(() => {
    if (!selected || typeof warehouseId !== 'number') {
      setSeries(null);
      setSeriesError(false);
      setLoadingSeries(false);
      return;
    }

    let cancelled = false;
    setLoadingSeries(true);
    setSeriesError(false);
    setSeries(null);

    void forecastReviewService
      .getDataValidationProductSeries(selected.product_id, {
        ...seriesQueryParams,
        warehouse_id: selected.warehouse_id,
      })
      .then((data) => {
        if (cancelled) return;
        setSeries(data);
      })
      .catch(() => {
        if (cancelled) return;
        setSeries(null);
        setSeriesError(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingSeries(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selected, warehouseId, seriesQueryParams]);

  const filterBarOptions: FilterOption[] = useMemo(() => {
    if (!filterOptions) return [];
    return [
      {
        id: 'anomaly_type',
        name: t('forecastModule.dataValidation.filters.anomalyType'),
        options: [
          { value: 'stockout', label: t('forecastModule.dataValidation.anomalyTypes.stockout') },
          { value: 'demand_outlier', label: t('forecastModule.dataValidation.anomalyTypes.demand_outlier') },
        ],
        multiple: false,
      },
      {
        id: 'product',
        name: t('forecastReview.filters.product'),
        options: [],
        loadOptionsRemotely: true,
        multiple: false,
      },
      {
        id: 'family',
        name: t('forecastReview.filters.family'),
        options: filterOptions.families.map((f) => ({ value: f.id, label: f.name })),
      },
      {
        id: 'subfamily',
        name: t('forecastReview.filters.subfamily'),
        options: filterOptions.subfamilies.map((s) => ({ value: s.id, label: s.name })),
      },
      {
        id: 'brand',
        name: t('forecastReview.filters.brand'),
        options: filterOptions.brands.map((b) => ({ value: b.id, label: b.name })),
      },
      {
        id: 'brand_code',
        name: t('forecastReview.filters.brandCode'),
        options: filterOptions.brand_codes.map((c) => ({ value: c, label: c })),
      },
      {
        id: 'supplier',
        name: t('forecastReview.filters.supplier'),
        options: filterOptions.suppliers.map((s) => ({ value: s.id, label: s.name })),
      },
    ];
  }, [filterOptions, t]);

  const fetchProductOptionsForFilter = useCallback(
    async (filterId: string, searchQuery: string) => {
      if (filterId !== 'product') return [];
      return forecastReviewService.getProductFilterSliceOrSearch({
        family: filters.family?.[0] ? Number(filters.family[0]) : undefined,
        brand: filters.brand?.[0] ? Number(filters.brand[0]) : undefined,
        supplier: filters.supplier?.[0] ? Number(filters.supplier[0]) : undefined,
        productQuery: searchQuery,
      });
    },
    [filters.family, filters.brand, filters.supplier]
  );

  const handleFilterChange = (
    newFilters: Record<string, string[]>,
    meta?: { selectedLabel?: { filterId: string; label: string } }
  ) => {
    setFilters(newFilters);
    if (!meta?.selectedLabel) return;
    const { filterId, label } = meta.selectedLabel;
    setActiveFilters((prev) => {
      const rest = prev.filter((f) => f.id !== filterId);
      if (!newFilters[filterId]?.length) return rest;
      return [...rest, { id: filterId, label, value: newFilters[filterId][0] ?? '' }];
    });
  };

  const handleClearFilter = (filterId: string) => {
    const next = { ...filters };
    delete next[filterId];
    setFilters(next);
    setActiveFilters((prev) => prev.filter((f) => f.id !== filterId));
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setActiveFilters([]);
  };

  const handleKpiFilterChange = (filter: DataValidationKpiFilter) => {
    setKpiFilter(filter);
    setProductsPage(1);
    setSelected(null);
  };

  const handleProductsPageChange = (page: number) => {
    setProductsPage(page);
    setSelected(null);
  };

  if (typeof warehouseId !== 'number') {
    return (
      <div className="p-4 md:p-6 text-sm text-gray-500">{t('forecastModule.dataValidation.selectWarehouse')}</div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {filterBarOptions.length > 0 ? (
        <FilterBar
          filters={filterBarOptions}
          onFilterChange={handleFilterChange}
          activeFilters={activeFilters}
          onClearFilter={handleClearFilter}
          onClearAllFilters={handleClearAllFilters}
          fetchOptionsForFilter={fetchProductOptionsForFilter}
        />
      ) : null}

      {loadingSummary ? (
        <div className="text-sm text-gray-500">{t('forecastModule.dataValidation.loadingSummary')}</div>
      ) : !summary?.step_available ? (
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center text-sm text-gray-500">
          {t('forecastModule.dataValidation.noStepData')}
        </div>
      ) : (
        <DataValidationKpiCards
          summary={summary}
          loading={loadingSummary}
          activeFilter={kpiFilter}
          onFilterChange={handleKpiFilterChange}
        />
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h4 className="text-sm font-medium text-gray-900">{t('forecastModule.dataValidation.productsTitle')}</h4>
        <p className="mt-0.5 mb-3 text-xs text-gray-500">{t('forecastModule.dataValidation.productsHint')}</p>
        <DataValidationProductsTable
          data={products}
          loading={loadingProducts}
          periodUnit={periodUnit ?? 'week'}
          selected={selected}
          onSelect={setSelected}
          onPageChange={handleProductsPageChange}
          onPageSizeChange={(size) => {
            setProductsPageSize(size);
            setProductsPage(1);
          }}
        />
      </div>

      {selected ? (
        <DataValidationProductPanel
          product={selected}
          warehouseLabel={warehouseLabel}
          series={series}
          loading={loadingSeries}
          error={seriesError}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </div>
  );
}
